import { X } from "lucide-react";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Button } from "@design-lab/shared/ui";
import { Modal, ModalBody, ModalHeader } from "@design-lab/shared/ui/modal";

type ConfirmActionModalProps = {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
};

export function ConfirmActionModal({
  isOpen,
  onConfirm,
  onClose,
  title = "Turn Off Background Removal?",
  message = "Disabling this will undo all background edits you’ve made. Do you want to continue?",
  confirmText = "Turn Off",
  cancelText = "Keep Changes"
}: ConfirmActionModalProps) {
  const { isDesktop } = useResponsive();

  const modalClassName = isDesktop
    ? "w-[420px] max-w-[90vw] h-auto"
    : "translate-none bottom-0 left-0 top-auto flex h-auto max-h-[90vh] w-full flex-col rounded-b-none";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={modalClassName}
      closeOnBackdropClick={false}
      closeOnEsc={false}
    >
      <ModalHeader className="justify-start border-0 p-4 pb-0 text-base font-medium text-gray-900">
        {title}
        <button
          onClick={onClose}
          className="absolute right-4 cursor-pointer"
          data-testid="modal-close-button"
        >
          <X className="h-6 w-6" />
        </button>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <p className="text-base text-gray-700">{message}</p>

        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            onPress={onConfirm}
            className="w-full border-blue-600 px-4 py-2.5 text-blue-600 hover:border-blue-700 hover:bg-blue-50 hover:text-blue-700"
          >
            {confirmText}
          </Button>
          <Button
            onPress={onClose}
            className="w-full bg-blue-600 px-4 py-2.5 text-white hover:bg-blue-700"
          >
            {cancelText}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
